import React from 'react';
import uniqBy from 'lodash/uniqBy';
import minBy from 'lodash/minBy';
import pure from 'recompose/pure';
import compose from 'recompose/compose';
import defaultProps from 'recompose/defaultProps';
import withHandlers from 'recompose/withHandlers';
import withPropsOnChange from 'recompose/withPropsOnChange';
import withState from 'recompose/withState';
import setDisplayName from 'recompose/setDisplayName';
import GoogleMapReact from 'google-map-react';
import _ from 'lodash';
import { MarkerType } from 'common/enums/MapEnum';
import LatLngUtils from 'common/utils/LatLngUtils';

import {
    vecAdd,
    tile2LatLng,
    latLng2Scaled,
    getTilesIds,
    getTileBounds
} from './utils';
import { unstable_batchedUpdates as reactUpdate } from 'react-dom'; // eslint-disable-line
import CanvasTile from './CanvasTile';
import { googleMapInstance } from 'common/utils/GoogleMap';

const TILE_SIZE = 256;
const mapStyle = {
    flex: '1 1 auto',
    display: 'flex',
    cursor: 'pointer'
};

export const canvasMapComponent = ({
    children,
    tiles,
    onMouseMove,
    renderMarkers,
    ...props
}) => {
    return (
        <div onMouseMove={onMouseMove} style={mapStyle}>
            <GoogleMapReact {...props} mapInstance={googleMapInstance}>
                {tiles.map(tile => (
                    <CanvasTile
                        {...tile}
                        key={`${tile.x} ${tile.y}`}
                        zoom={props.zoom}
                        renderMarkers={renderMarkers}
                    />
                ))}
                {children}
            </GoogleMapReact>
        </div>
    );
};

export const canvasMapHOC = compose(
    defaultProps({
        bboxSize: 40,
        hoverDistance: 30,
        onMarkerMouseEnter: () => {},
        onMarkerMouseLeave: () => {},
        onDrag: () => {},
        markers: [],
        distanceToMouseKoef: () => 1
    }),
    pure,
    withState('mapData', 'setMapData', {}),
    withHandlers(() => {
        let boundingRect_;
        let hoveredMarker_;
        let lastDragTime_ = 0;
        let lastBounds;

        return {
            onMouseMove: ({
                mapData: { bounds, zoom },
                markers,
                hoverDistance,
                onMarkerMouseEnter,
                onMarkerMouseLeave,
                distanceToMouseKoef,
                calculateLatLngFromMousePos
            }) => e => {
                // highly unoptimized distance checker
                if (!bounds) {
                    if (hoveredMarker_) {
                        onMarkerMouseLeave(hoveredMarker_);
                    }
                    hoveredMarker_ = undefined;
                    return;
                }
                // can be debounced to reduce calculations
                boundingRect_ =
                    lastBounds !== bounds || !boundingRect_
                        ? e.currentTarget.getBoundingClientRect()
                        : boundingRect_;
                lastBounds = bounds;
                // #TODO: figure out how this was working. bounding rect needs to be updated/cleared on resize
                const currTime = new Date().getTime();

                const NO_HOVER_AFTER_DRAG_TIMEOUT = 100; // to prevent hovers at drag

                if (currTime - lastDragTime_ < NO_HOVER_AFTER_DRAG_TIMEOUT) {
                    if (hoveredMarker_) {
                        onMarkerMouseLeave(hoveredMarker_);
                    }
                    hoveredMarker_ = undefined;
                    lastDragTime_ = currTime;
                    return;
                }

                if (markers.length === 0) {
                    if (hoveredMarker_) {
                        onMarkerMouseLeave(hoveredMarker_);
                    }
                    hoveredMarker_ = undefined;
                    return;
                }

                const mousePos = {
                    x: e.clientX - boundingRect_.left,
                    y: e.clientY - boundingRect_.top
                };

                // convert 2 tile coords
                const mouseLatLng = calculateLatLngFromMousePos(mousePos);
                const mousePixelCoordinates = LatLngUtils.latLngToPixel(
                    mouseLatLng,
                    zoom
                );

                const hoverableMarkers = markers.filter(marker => {
                    return marker.props.markerType !== MarkerType.NON_HOVERABLE;
                });
                // find nearest check that it inside distance
                const nearestMarker = minBy(hoverableMarkers, ({ props }) => {
                    return (
                        Math.pow(mouseLatLng.lat - props.lat, 2) +
                        Math.pow(mouseLatLng.lng - props.lng, 2)
                    );
                });
                // distance in pixels
                if (nearestMarker) {
                    const { props: nearestMarkerProps } = nearestMarker;
                    const nearestMarkerPixels = LatLngUtils.latLngToPixel(
                        nearestMarkerProps,
                        zoom
                    );
                    const distanceInPixels =
                        distanceToMouseKoef(nearestMarkerProps) *
                        LatLngUtils.getDistanceBetweenTwoPixels(
                            nearestMarkerPixels,
                            mousePixelCoordinates
                        );

                    if (distanceInPixels < hoverDistance) {
                        if (hoveredMarker_ !== nearestMarker) {
                            if (hoveredMarker_) {
                                onMarkerMouseLeave(hoveredMarker_);
                            }

                            hoveredMarker_ = nearestMarker;
                            onMarkerMouseEnter(nearestMarker);
                        }
                    } else {
                        if (hoveredMarker_) {
                            onMarkerMouseLeave(hoveredMarker_);
                        }
                        hoveredMarker_ = undefined;
                    }
                }
            },
            onChange: ({ onChange, setMapData, tileExpand = 2 }) => (
                mapProps,
                ...args
            ) => {
                reactUpdate(() => {
                    onChange(mapProps, ...args);
                    const { bounds, zoom } = mapProps;
                    const ids = getTilesIds({ bounds, zoom, tileExpand });
                    const tileBounds = getTileBounds({
                        bounds,
                        zoom,
                        tileExpand
                    });
                    setMapData({ ids, tileBounds, bounds, zoom });
                });
            },
            onDrag: ({ onDrag }) => (...args) => {
                lastDragTime_ = new Date().getTime();
                onDrag(...args);
            }
        };
    }),
    withPropsOnChange(
        (props, nextProps) => {
            return (
                !_.isEqual(props.markers, nextProps.markers) ||
                props.mapData !== nextProps.mapData ||
                props.bboxSize !== nextProps.bboxSize ||
                props.refresh !== nextProps.refresh
            );
        },
        ({ markers, mapData: { zoom, tileBounds }, bboxSize = 40 }) => {
            if (!tileBounds) {
                return {
                    tiles: []
                };
            }

            const [from, to] = tileBounds;
            const bigN = Math.pow(2, 26);
            const tiles = {};

            markers.forEach(marker => {
                const pt = latLng2Scaled(marker.props, zoom);
                const bb = bboxSize / 2 / TILE_SIZE;

                const allPts = [
                    pt,
                    vecAdd(pt, { x: -bb, y: bb }),
                    vecAdd(pt, { x: bb, y: bb }),
                    vecAdd(pt, { x: bb, y: -bb }),
                    vecAdd(pt, { x: -bb, y: -bb })
                ];

                const uniqTiles = uniqBy(
                    allPts.map(({ x, y }) => {
                        return {
                            x: Math.floor(x),
                            y: Math.floor(y)
                        };
                    }),
                    ({ x, y }) => {
                        return x * bigN + y;
                    }
                ).filter(
                    ({ x, y }) =>
                        from.x <= x && x <= to.x && from.y <= y && y <= to.y
                );

                uniqTiles.forEach(({ x, y }) => {
                    const key = `${x} ${y}`;
                    if (!tiles[key]) {
                        tiles[key] = {
                            ...tile2LatLng({ x, y }, zoom),
                            seLatLng: {
                                ...tile2LatLng({ x: x + 1, y: y + 1 }, zoom)
                            },
                            x,
                            y,
                            markers: []
                        };
                    }
                    tiles[key].markers.push(marker);
                });
            });

            return {
                tiles: Object.values(tiles)
            };
        }
    )
);
const CanvasMap = canvasMapHOC(canvasMapComponent);
setDisplayName('CanvasMap')(CanvasMap);

export default CanvasMap;
